import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import { userLogin, companyLogin, forgotPassword, verifyOtp, selectApplicationContext } from "./auth.api";
import { EmailValidate, FormValidate } from "../../../components/formValidate";
import ResetPasswordModal from "../../../layout/userlayout/components/header/resetPasswordModal";
import jwtDecode from "jwt-decode";
import OTPInput from "otp-input-react";
import "./login.css";
import "../otp/otp.css";

export default function Login(props) {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(props.defaultTab || (history.location.pathname === "/company-login" ? "staff" : "user"));
  
  // States
  const [loginParams, setLoginParams] = useState({}); // User OTP mobile login
  const [formData, setFormData] = useState({ email: "", password: "" }); // Staff login
  const [mobileError, setMobileError] = useState(false);
  const [staffError, setStaffError] = useState({});
  const [submit, setSubmit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState("");
  const [pendingRedirectPath, setPendingRedirectPath] = useState("");

  // OTP Phase States
  const [OTP, setOTP] = useState("");
  const [otpStep, setOtpStep] = useState("mobile"); // "mobile" or "otp"
  const [records, setRecords] = useState([]);
  const [tempToken, setTempToken] = useState("");
  const [showSelection, setShowSelection] = useState(false);

  // Mobile change handler
  const handleMobileChange = (e) => {
    var ticket = e.target.value;
    const sequentialPattern = /(012345|123456|234567|345678|456789|987654|876543|765432|654321|543210|999999|111111111|2222222|33333333|44444444|55555555|6666666666|7777777777|8888888)/;
    const phoneNumberRegex = /^(?!(\d)\1{9})[6-9]\d{9}$/;
    var reg = new RegExp("^[0-9]*$");

    if (e.target.validity.patternMismatch) {
      e.target.setCustomValidity("Please enter valid 10 digit number");
    } else {
      e.target.setCustomValidity("");
    }
    if (reg.test(ticket) && phoneNumberRegex.test(ticket) && !sequentialPattern.test(ticket)) {
      setLoginParams({ [e.target.name]: ticket });
      setMobileError(false);
    } else {
      setMobileError(true);
      setLoginParams({ [e.target.name]: ticket });
    }
  };

  // Staff credentials change handler
  const handleStaffChange = (e) => {
    let erroremail = false;
    const { name, value } = e.target;

    if (name === "email") {
      erroremail = value ? !EmailValidate(value) : false;
      setStaffError({ ...staffError, email: erroremail });
    }
    if (name === "password") {
      setStaffError((prev) => ({ ...prev, password: !value }));
    }
    setFormData({ ...formData, [name]: value });
  };

  // Mobile User Submit (OTP request)
  const verifyUser = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmit(true);
    if (!mobileError) {
      userLogin(loginParams)
        .then((res) => {
          setSnackMessage(res.message || "OTP Sent Successfully");
          setIsSnackError(false);
          setSnackOpen(true);
          setIsLoading(false);
          setSubmit(false);
          setOtpStep("otp"); // Transition to OTP code entry field
        })
        .catch((err) => {
          setSubmit(false);
          setSnackMessage(err.response?.data?.message || "Login failed");
          setIsSnackError(true);
          setSnackOpen(true);
          setIsLoading(false);
        });
    } else {
      setSnackMessage("Enter a valid Phone number");
      setIsSnackError(true);
      setSnackOpen(true);
      setIsLoading(false);
      setSubmit(false);
    }
  };

  // Resend OTP handler
  const resendOtp = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setSubmit(true);
    userLogin(loginParams)
      .then((res) => {
        setSnackMessage(res.message || "OTP resent successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        setIsLoading(false);
        setSubmit(false);
      })
      .catch((err) => {
        setSubmit(false);
        setSnackMessage(err.response?.data?.message || "Failed to resend OTP");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsLoading(false);
      });
  };

  // Verify OTP submit handler
  const handleOtpVerify = (e) => {
    e.preventDefault();
    setSubmit(true);
    setIsLoading(true);

    let data = {
      mobile_number: loginParams.mobile_number,
      otp_value: OTP,
    };

    verifyOtp(data)
      .then((res) => {
        if (res.multiple_records_exist) {
          setRecords(res.records || []);
          setTempToken(res.temp_token);
          setShowSelection(true);
          setIsLoading(false);
          setSubmit(false);
        } else {
          completeLogin(res.token);
        }
      })
      .catch((err) => {
        setSubmit(false);
        setIsLoading(false);
        setSnackMessage(err.response?.data?.message || "Verification failed");
        setIsSnackError(true);
        setSnackOpen(true);
      });
  };

  const handleBackToMobile = () => {
    setOtpStep("mobile");
    setOTP("");
  };

  // Staff Submit (Username/Password)
  const handleStaffSubmit = (e) => {
    e.preventDefault();
    setSubmit(true);
    setIsLoading(true);
    const requiredFields = ["email", "password"];
    const errObj = FormValidate(formData, requiredFields);
    setStaffError(errObj);

    if (Object.keys(errObj).length === 0) {
      companyLogin(formData)
        .then((res) => {
          setSubmit(false);
          setIsLoading(false);
          sessionStorage.setItem("token", res.token);

          const decodedToken = jwtDecode(res.token);
          const isCompanyUser =
            decodedToken?.actor === "company_user" ||
            decodedToken?.user?.is_company_user === true ||
            decodedToken?.is_company_user === true ||
            decodedToken?.data?.is_company_user === true;
          const shouldShowResetPrompt =
            Number(
              decodedToken?.is_initial_login ??
                decodedToken?.user?.is_initial_login ??
                decodedToken?.data?.is_initial_login
            ) === 0;

          const userType = Number(decodedToken.user_type || decodedToken.user?.user_type || decodedToken.sub);

          const dashboardRoles = [0, 4, 5, 6, 8, 9, 10, 24];
          const adminRoles = [1, 2, 23];
          const publicRoles = [21, 27];
          let redirectPath = "/dashboard";

          if (adminRoles.includes(userType)) {
            props.adminLogin(res.token);
            redirectPath = "/admin-dashboard";
          } else if (dashboardRoles.includes(userType)) {
            props.login(res.token);
            redirectPath = "/dashboard";
          } else if (publicRoles.includes(userType)) {
            props.login(res.token);
            redirectPath = "/dashboard";
          } else {
            props.login(res.token);
            redirectPath = "/dashboard";
          }

          if (isCompanyUser && shouldShowResetPrompt) {
            sessionStorage.setItem("company_user_reset_password_prompt", "1");
            setResetPasswordToken(res.token);
            setPendingRedirectPath(redirectPath);
            setShowResetPassword(true);
          } else {
            sessionStorage.removeItem("company_user_reset_password_prompt");
            props.history.push(redirectPath);
          }
        })
        .catch((error) => {
          setSubmit(false);
          setIsLoading(false);
          setSnackMessage(error.response?.data?.message || "Login failed");
          setIsSnackError(true);
          setSnackOpen(true);
        });
    } else {
      setSnackMessage("Please fill all mandatory fields.");
      setIsSnackError(true);
      setSnackOpen(true);
      setIsLoading(false);
      setSubmit(false);
    }
  };

  // Staff Forgot Password Submit
  const handleForgotPassword = () => {
    if (!EmailValidate(forgotEmail)) {
      setSnackMessage("Please enter a valid email");
      setIsSnackError(true);
      setSnackOpen(true);
      return;
    }

    forgotPassword({ email: forgotEmail })
      .then((res) => {
        setSnackMessage("Password reset instructions sent to your email.");
        setIsSnackError(false);
        setSnackOpen(true);
        setShowForgot(false);
      })
      .catch((error) => {
        setSnackMessage(
          error?.response?.data?.message || "Failed to process request"
        );
        setIsSnackError(true);
        setSnackOpen(true);
      });
  };

  const handleForgot = () => {
    setShowForgot(!showForgot);
  };

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const handleResetPasswordClose = () => {
    setShowResetPassword(false);
    sessionStorage.removeItem("company_user_reset_password_prompt");

    if (pendingRedirectPath) {
      const nextPath = pendingRedirectPath;
      setPendingRedirectPath("");
      props.history.push(nextPath);
    }
  };

  const completeLogin = (token) => {
    sessionStorage.setItem("token", token);
    props.login(token);

    const activelocation = sessionStorage.getItem("next_path");
    const activePath = activelocation ?? "/dashboard";
    let targetPath = activePath;

    if (!activelocation) {
      try {
        const userData = jwtDecode(token);
        const userType = Number(userData?.user_type || userData?.user?.user_type || userData?.sub);
        const dashboardRoles = [0, 4, 5, 6, 8, 9, 10, 24];
        const publicRoles = [21, 27];

        if (dashboardRoles.includes(userType)) {
          targetPath = "/dashboard";
        } else if (publicRoles.includes(userType)) {
          targetPath = "/dashboard";
        }
      } catch (e) {
        console.error("Token decoding error:", e);
      }
    }

    sessionStorage.removeItem("next_path");
    props.history.push(targetPath);
  };

  // Account Context Selection Helper Functions
  const handleSelectApplication = (record) => {
    setIsLoading(true);
    const data = {
      application_id: record.type === "application" ? record.id : null,
      company_id: record.type === "company" ? record.id : null,
    };

    selectApplicationContext(data, tempToken)
      .then((res) => {
        if (res.success) {
          if (res.context) {
            localStorage.setItem("context", JSON.stringify(res.context));
          }
          completeLogin(res.token);
        } else {
          setIsLoading(false);
          setSnackMessage(res.message || "Failed to select application");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        setSnackMessage(err.response?.data?.message || "Selection error");
        setIsSnackError(true);
        setSnackOpen(true);
      });
  };

  const getApplicationDisplayName = (record) => {
    const refNo = String(record?.unique_application_ref_no || "").toUpperCase();

    if (refNo.startsWith("KSPACE-PREMISES_")) {
      return "KSPACE Premises";
    }

    if (
      refNo.startsWith("KSPACE-OWN_") ||
      refNo.startsWith("KSPACE-GENERAL_")
    ) {
      return "KSPACE - Own-Premises";
    }

    if (refNo.startsWith("KSPACE/E&IT/AFF/")) {
      return "AFFLIATED COMPANY";
    }

    return record?.type || "Application";
  };

  const getApplicationTypeIcon = (type) => {
    switch (type?.toLowerCase()) {
      case "kerala":
        return (
          <div className="type-icon-small kerala-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
          </div>
        );
      case "india":
        return (
          <div className="type-icon-small india-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
            </svg>
          </div>
        );
      default:
        return (
          <div className="type-icon-small generic-bg">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
          </div>
        );
    }
  };

  const RecordIcon = ({ record }) => {
    if (record.logo) {
      const servInstance = process.env.REACT_APP_BASE_URL;
      const result = servInstance.substring(0, servInstance?.length - 4);
      return (
        <div className="record-logo-wrapper">
          <img src={`${result}${record.logo}`} alt={record.company_name} onError={(e) => e.target.style.display = 'none'} />
        </div>
      );
    }

    const firstLetter = record.company_name?.charAt(0).toUpperCase() || "?";
    const colors = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
    const colorIndex = firstLetter.charCodeAt(0) % colors.length;

    return (
      <div className="record-avatar" style={{ background: colors[colorIndex] }}>
        <span>{firstLetter}</span>
      </div>
    );
  };

  if (showSelection) {
    return (
      <div className="login-page-wrapper">
        <Loader open={isLoading} />
        <div className="login-unified-container selection-active-container">
          <div className="selection-body premium-selection w-100">
            <div className="selection-header-premium">
              <h2>Welcome!</h2>
              <p>Your primary mobile number "<b>{loginParams.mobile_number}</b>" is linked to multiple profiles. Please select the profile you wish to access and continue to your dashboard.</p>
            </div>
            <div className="records-grid-glass">
              {records.map((record, index) => (
                <div key={index} className="glass-card animated-card" onClick={() => handleSelectApplication(record)}>
                  <div className="glass-card-header">
                    <div className="glass-icon-wrapper">
                      <RecordIcon record={record} />
                      {getApplicationTypeIcon(record.type)}
                    </div>
                  </div>
                  <div className="glass-card-body">
                    <h3 className="company-name-glass">{record.company_name}</h3>
                    <div className="glass-ref-row">
                      <span className="glass-label">REF ID</span>
                      <span className="glass-value">{record.unique_application_ref_no || `ID: ${record.id}`}</span>
                    </div>
                  </div>
                  <div className="glass-card-footer">
                    <div className="glass-type-tag">
                      {getApplicationDisplayName(record)}
                    </div>
                    <div className="glass-action">
                      <span>Login</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {records.length > 4 && (
              <div className="scroll-hint-glass">
                <span>Scroll for more</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7 13l5 5 5-5M7 6l5 5 5-5" />
                </svg>
              </div>
            )}
            <div className="selection-footer-premium">
              <button className="btn-back-minimal" onClick={() => setShowSelection(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12"></line>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                Go Back
              </button>
            </div>
          </div>
          <Snackbar
            message={snackMessage}
            error={isSnackError}
            open={snackOpen}
            close={snackbarClose}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="login-page-wrapper">
        <Loader open={isLoading} />
        
        <div className="login-unified-container">
          {/* Left Column: Branding & Portal Information */}
          <div className="login-info-column">
            <div className="login-info-brand">
              <img src="/latest-logo.png" alt="Kerala Government" className="info-brand-logo" />
              <div className="info-brand-divider"></div>
              <h1 className="info-brand-title">Kerala Spacepark</h1>
            </div>
            
            <div className="login-info-content">
              <h2 className="info-welcome-title">Welcome to the KSPACE Portal</h2>
              <p className="info-welcome-text">
                Access your dashboard, manage resources, and connect with academic institutions and affiliated companies registered with Kerala Spacepark.
              </p>
              
              <div className="info-features-list">
                <div className="info-feature-item">
                  <div className="info-feature-icon">
                    <i className="fa fa-building"></i>
                  </div>
                  <div className="info-feature-text">
                    <strong>Affiliated Companies</strong>
                    <span>Review space technology projects and collaboration requests.</span>
                  </div>
                </div>
                
                <div className="info-feature-item">
                  <div className="info-feature-icon">
                    <i className="fa fa-graduation-cap"></i>
                  </div>
                  <div className="info-feature-text">
                    <strong>Academic Institutions</strong>
                    <span>Submit internship requests and student programs directly.</span>
                  </div>
                </div>
                
                <div className="info-feature-item">
                  <div className="info-feature-icon">
                    <i className="fa fa-shield-alt"></i>
                  </div>
                  <div className="info-feature-text">
                    <strong>Secure Access</strong>
                    <span>Multi-factor authentication (OTP) and role-based permissions.</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="login-info-footer">
              <span>© {new Date().getFullYear()} Kerala Spacepark (KSPACE). All rights reserved.</span>
            </div>
          </div>

          {/* Right Column: Dynamic Login Forms Card */}
          <div className="login-form-column">
            <div className="login-card-container">
              {/* Tab Switcher */}
              <div className="login-tab-switcher">
                <button
                  type="button"
                  className={`login-tab-btn ${activeTab === "user" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("user");
                    setShowForgot(false);
                  }}
                >
                  <i className="fa fa-user"></i>
                  User Login
                </button>
                <button
                  type="button"
                  className={`login-tab-btn ${activeTab === "staff" ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab("staff");
                    setShowForgot(false);
                  }}
                >
                  <i className="fa fa-briefcase"></i>
                  Staff Login
                </button>
              </div>

              {/* Form Content */}
              <div className="login-card-body">
                {activeTab === "user" ? (
                  otpStep === "mobile" ? (
                    <form onSubmit={verifyUser} className="login-form-anim">
                      <div className="form-avatar-header">
                        <div className="avatar-circle">
                          <i className="fa fa-user-circle"></i>
                        </div>
                        <h3 className="form-title">User Portal</h3>
                        <p className="form-subtitle">Enter your registered mobile number to proceed</p>
                      </div>

                      <div className="form-group-custom">
                        <label className="form-input-label">Mobile Number</label>
                        <div className="mobile-input-group">
                          <span className="mobile-country-code">+91</span>
                          <input
                            type="tel"
                            className={`form-input-field ${mobileError ? "error" : ""}`}
                            name="mobile_number"
                            maxLength="10"
                            minLength="10"
                            pattern="[0-9]{10}"
                            placeholder="Registered mobile"
                            required
                            value={loginParams.mobile_number ?? ""}
                            onChange={handleMobileChange}
                          />
                        </div>
                        {mobileError && (
                          <span className="form-error-msg">Please enter a valid 10-digit mobile number</span>
                        )}
                      </div>

                      <button
                        className="login-submit-button"
                        type="submit"
                        disabled={submit}
                      >
                        <span>Send OTP</span>
                        <i className="fa fa-paper-plane"></i>
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleOtpVerify} className="login-form-anim">
                      <div className="form-avatar-header">
                        <div className="avatar-circle">
                          <i className="fa fa-shield-alt"></i>
                        </div>
                        <h3 className="form-title">Enter Verification Code</h3>
                        <p className="form-subtitle">We have sent a 6-digit OTP to +91 {loginParams.mobile_number}</p>
                      </div>

                      <div className="form-group-custom d-flex flex-column align-items-center justify-content-center">
                        <OTPInput
                          value={OTP}
                          onChange={setOTP}
                          autoFocus
                          OTPLength={6}
                          otpType="number"
                          disabled={false}
                          secure={true}
                          inputClassName="otp-field-input"
                          className="otp-input-react-container"
                        />
                      </div>

                      <button
                        className="login-submit-button"
                        type="submit"
                        disabled={submit || OTP.length < 6}
                      >
                        <span>Verify OTP</span>
                        <i className="fa fa-check-circle"></i>
                      </button>

                      <div className="d-flex justify-content-between mt-4">
                        <span className="form-link-back" onClick={handleBackToMobile}>
                          <i className="fa fa-arrow-left"></i> Change Number
                        </span>
                        <span className="form-link-resend" onClick={resendOtp}>
                          Resend OTP <i className="fa fa-sync-alt"></i>
                        </span>
                      </div>
                    </form>
                  )
                ) : !showForgot ? (
                  <form onSubmit={handleStaffSubmit} className="login-form-anim">
                    <div className="form-avatar-header">
                      <div className="avatar-circle staff">
                        <i className="fa fa-lock"></i>
                      </div>
                      <h3 className="form-title">Staff Portal</h3>
                      <p className="form-subtitle">Sign in using your corporate credentials</p>
                    </div>

                    <div className="form-group-custom">
                      <label className="form-input-label">Username / Email</label>
                      <div className="input-with-icon">
                        <i className="fa fa-envelope input-icon-left"></i>
                        <input
                          type="text"
                          className={`form-input-field pad-left ${staffError.email ? "error" : ""}`}
                          name="email"
                          placeholder="username@kspace.org"
                          value={formData.email}
                          onChange={handleStaffChange}
                          required
                        />
                      </div>
                      {staffError.email && (
                        <span className="form-error-msg">Please enter a valid email address</span>
                      )}
                    </div>

                    <div className="form-group-custom">
                      <label className="form-input-label">Password</label>
                      <div className="input-with-icon">
                        <i className="fa fa-key input-icon-left"></i>
                        <input
                          type="password"
                          className={`form-input-field pad-left ${staffError.password ? "error" : ""}`}
                          name="password"
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={handleStaffChange}
                          required
                        />
                      </div>
                      {staffError.password && (
                        <span className="form-error-msg">Password is required</span>
                      )}
                    </div>

                    <button
                      className="login-submit-button staff-btn"
                      type="submit"
                      disabled={submit}
                    >
                      <span>Sign In</span>
                      <i className="fa fa-sign-in-alt"></i>
                    </button>
                  </form>
                ) : (
                  <form onSubmit={(e) => { e.preventDefault(); handleForgotPassword(); }} className="login-form-anim">
                    <div className="form-avatar-header">
                      <div className="avatar-circle forgot">
                        <i className="fa fa-key"></i>
                      </div>
                      <h3 className="form-title">Forgot Password</h3>
                      <p className="form-subtitle">Enter your email to request reset link</p>
                    </div>

                    <div className="form-group-custom">
                      <label className="form-input-label">Corporate Email</label>
                      <div className="input-with-icon">
                        <i className="fa fa-envelope input-icon-left"></i>
                        <input
                          type="email"
                          className="form-input-field pad-left"
                          name="forgotEmail"
                          placeholder="username@kspace.org"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                        />
                      </div>
                      <div className="mt-2 text-end">
                        <span className="form-forgot-link" onClick={handleForgot}>
                          Back to Sign In
                        </span>
                      </div>
                    </div>

                    <button
                      className="login-submit-button"
                      type="submit"
                    >
                      <span>Submit Request</span>
                      <i className="fa fa-paper-plane"></i>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          <Snackbar
            message={snackMessage}
            error={isSnackError}
            open={snackOpen}
            close={snackbarClose}
          />
        </div>
      </div>

      <ResetPasswordModal
        show={showResetPassword}
        onHide={handleResetPasswordClose}
        token={resetPasswordToken || props.state?.payload}
      />
    </>
  );
}
